/**
 * The browser shell: one canvas behind a React root. The page starts in the
 * town (integration plan slice 6b: `?scene=<name>`, default `la_tirana`, and
 * `?spawn=<id>`) behind the login (ADR 0034: no saved session, or one the server
 * refuses; development's `?scene=` without a token skips it as a guest, for
 * the tests); flying from the airship starts the server's run on the same
 * canvas and its end brings the deck back (`src/flow.ts`, ADR 0032, 0033,
 * 0034), and a run the server still has as being played resumes once logged
 * in (the town waits for that answer: no answer holds it under a retry row; a
 * run this build cannot play is kept and asked about, `account.ts` `run()`).
 * `?run=1` starts straight in a **local, unverified** run of the dungeon
 * instead (`?seed=<n>`, default 1; the dungeon tests' start; no account, no
 * server; such a run stays when it ends). The view's tier is `?tier=off|medium`, else a remembered choice,
 * else the capability check (`src/tier.ts`). The town's time of day is
 * `?time=day|night`, else the pause menu's remembered choice, else day
 * (`src/time.ts`, ADR 0032).
 * `?stats=1` shows the stats panel for the volunteer device test (the pause
 * menu shows and hides it too). Uncaught errors and unhandled rejections are
 * reported to `POST /api/client-errors` (`src/client-errors.ts`, ADR 0035 D10;
 * the stats panel's report is unchanged).
 *
 * Locales (integration plan slice 5): the page shows `?lang=en|zh-Hans|ja`,
 * else the language the player chose (the pause menu's language row since
 * slice 6b), else the browser's (`src/locale.ts`). `<html lang>` follows it
 * (CJK glyph choice, and the self-hosted fonts' `:lang` stacks in
 * `public/fonts/fonts.css` and `shell.css`), and so does the page title.
 *
 * Touch (integration plan slice 4): the pad shows on a device with a coarse
 * pointer or a touch screen, or with `?pad=1` (`<html class="ab-touch">`
 * then, so the dialogue's strip sits above the pad's row); taps and swipes on
 * the canvas go through `src/touch.ts`. The canvas takes a fixed CSS size from
 * the visual viewport (the Rendering page's "iOS Safari": a WebGL buffer
 * resized on every scroll that shows or hides the address bar leaks), and
 * takes a new one only once the viewport has kept it for
 * `uiTiming.resizeSettleMs` (a rotation, a window resize, the address bar
 * settling): one `resize` of the view per settled size. The page does not
 * zoom (`index.html`'s viewport: `maximum-scale=1, user-scalable=no`); where a
 * browser zooms anyway, a zoomed visual viewport (`scale` not 1) is not a new
 * size: the canvas keeps the last unzoomed one.
 *
 * The retro kit's panels blur the canvas behind them; where the browser
 * cannot (`CSS.supports`), `<html class="ab-no-blur">` gives them the darker
 * fill alone.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { uiTiming } from '@ab/ui';
import type { Locale } from '@ab/session';
import { pack, scenes } from 'virtual:ab-content';
import { playable } from './controller.ts';
import { type FlowMode, createFlow } from './flow.ts';
import { chooseLocale, rememberLocale } from './locale.ts';
import { tables } from './shared.ts';
import { App } from './shell.tsx';
import { captureErrors, exposeAppHook, exposeHook, exposeSceneHook, exposeShipHook } from './test-hook.ts';
import { type AccountStatus, createAccountStore } from './account.ts';
import { createApi } from './api.ts';
import { reportClientErrors } from './client-errors.ts';
import { createServerTime } from './clock.ts';
import { bindGestures, bindTouch } from './touch.ts';
import { chooseTier } from './tier.ts';
import { chooseTime } from './time.ts';
import './shell.css';

const params = new URLSearchParams(location.search);
const hookEnabled = import.meta.env.DEV || params.get('test') === '1';
const errors = hookEnabled ? captureErrors() : [];
/** The server's time as its answers say it (ADR 0003, 0034), the API on this origin, and the account. */
const time = createServerTime();
const api = createApi(time);
// The page's uncaught errors go to our own API (ADR 0035 D10), at most 5 per load; nothing else is ever sent.
reportClientErrors({ token: () => api.token(), locale: () => document.documentElement.lang });
/**
 * Who the page plays as (ADR 0034): `?run=1` is a local, unverified run that needs no account; a saved token is
 * checked first; development's `?scene=` without a token walks as a guest (the tests' hook: the ship empty, a
 * flight refused); otherwise the login comes first.
 */
const runFirst = params.get('run') === '1';
const devScene = import.meta.env.DEV && params.get('scene') !== null;
const startStatus: AccountStatus = runFirst ? 'guest' : api.token() !== null ? 'checking' : devScene ? 'guest' : 'signed_out';
const account = createAccountStore(api, time, startStatus, { playable });

/** `?seed=` as an `enter_floor` seed in [0, 2^32); 1 when absent or invalid. */
const seedParam = (text: string | null): number => {
  const n = Number(text ?? '');
  return text !== null && /^\d+$/.test(text) && n < 2 ** 32 ? n : 1;
};

const canvas = document.getElementById('scene');
const root = document.getElementById('root');
if (!(canvas instanceof HTMLCanvasElement) || root === null) throw new Error('index.html needs #scene (a canvas) and #root');

type Size = { readonly width: number; readonly height: number };
/** The visual viewport's size in whole CSS pixels (the window's where there is none); null while it is zoomed. */
const viewportSize = (): Size | null => {
  const v = window.visualViewport;
  if (v !== null && v.scale !== 1) return null;
  return { width: Math.round(v?.width ?? window.innerWidth), height: Math.round(v?.height ?? window.innerHeight) };
};
/** A page loaded zoomed starts from the layout viewport's size. */
let canvasSize: Size = viewportSize() ?? { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight };
const sizeCanvas = (): void => {
  canvas.style.width = `${canvasSize.width}px`;
  canvas.style.height = `${canvasSize.height}px`;
};
sizeCanvas();
const tier = chooseTier(params);
const startLocale = chooseLocale(params, navigator.languages);
const coarse = window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
const touch = params.get('pad') === '1' || coarse;
if (touch) document.documentElement.classList.add('ab-touch');
if (!CSS.supports('backdrop-filter', 'blur(1px)') && !CSS.supports('-webkit-backdrop-filter', 'blur(1px)')) document.documentElement.classList.add('ab-no-blur');

// The panels' colours come from the dungeon's theme in both modes (the retro kit's one look).
const theme = pack.themes[pack.dungeons['dungeon.old_quarry']?.theme ?? ''];
if (theme !== undefined) {
  document.documentElement.style.setProperty('--ab-text', theme.palette.exit);
  document.documentElement.style.setProperty('--ab-panel', theme.palette.background);
  // The overlays' colours (labels, badges, banners): the theme's flashes.
  for (const key of ['hit', 'heal', 'cast', 'status'] as const) document.documentElement.style.setProperty(`--ab-${key}`, theme.palette[key]);
  // The minimap's colours (slice 4): explored floor, walls, the exit, the leader's dot and its outline.
  const mapColours = { floor: 'trim', wall: 'iron', exit: 'exit', leader: 'party', edge: 'background' } as const;
  for (const [name, key] of Object.entries(mapColours)) document.documentElement.style.setProperty(`--ab-map-${name}`, theme.palette[key]);
}

/**
 * `?scene=<name>` (default `la_tirana`) and `?spawn=<id>`; `?run=1` starts in a run instead. A name the content
 * pack has no scene for starts the town, with a warning (never a silent dungeon).
 */
const defaultScene = 'la_tirana';
const asked = params.get('scene');
const known = asked !== null && scenes[`scene.${asked}`] !== undefined;
if (asked !== null && !known) console.warn(`?scene=${asked}: no such scene; starting in ${defaultScene}`);
const sceneName = known ? asked : defaultScene;
/** Gestures on the canvas follow the mode: the dungeon's taps and swipes, or a scene's taps (a swipe does nothing there). */
let unbind = (): void => undefined;
const onMode = (mode: FlowMode): void => {
  unbind();
  if (mode.kind === 'run') {
    unbind = bindTouch(canvas, mode.run);
    if (hookEnabled) exposeHook(mode.run, errors, canvas);
    return;
  }
  const scene = mode.scene;
  unbind = bindGestures(canvas, {
    tap: (x, y) => {
      scene.tap(x, y);
    },
    swipe: () => undefined,
  });
  if (hookEnabled) exposeSceneHook(scene, errors, canvas);
};
if (hookEnabled) exposeShipHook(account, () => flow.snapshot().mode);
const flow = createFlow({
  canvas,
  start: runFirst ? { kind: 'run', seed: seedParam(params.get('seed')) } : { kind: 'scene', scene: `scene.${sceneName}`, spawn: params.get('spawn') ?? undefined },
  tier,
  locale: startLocale,
  time: chooseTime(params),
  stats: params.get('stats') === '1',
  account,
  onMode,
});
if (hookEnabled) exposeAppHook(flow);

/**
 * Once logged in (a saved token checked, or the login; after a temporary password, once the new one is set) in a
 * scene: the server is asked for a run still being played
 * (ADR 0002's mid-run save), and the town waits for its answer (`account.run()`); a playable one resumes (the
 * verified floors replayed, the current floor entered with its seed). Not under `?scene=`, which names where the page
 * starts, and not mid-run (a login after an `unauthorized` there sends the floor again instead).
 */
account.onResume((run) => {
  flow.resume(run);
});
let wasIn = false;
const onAccount = (): void => {
  // Not while a new password is owed (ADR 0038 D8): the server refuses the run until then.
  const isIn = account.status() === 'signed_in' && !account.mustChangePassword();
  if (isIn && !wasIn && params.get('scene') === null && flow.snapshot().mode.kind === 'scene') void account.checkRun();
  wasIn = isIn;
};
account.subscribe(onAccount);
if (startStatus === 'checking') void account.refresh();

/** `<html lang>` and the title follow the locale the page shows. */
const showLocale = (): void => {
  const locale = flow.locale();
  document.documentElement.lang = locale;
  document.title = tables[locale]['ui.shell.title'] ?? document.title;
};
showLocale();
/** A language chosen in the pause menu: the text switches at once and the choice is remembered (only a player's choice is). */
const chooseLanguage = (locale: Locale): void => {
  rememberLocale(locale);
  flow.setLocale(locale);
  showLocale();
};

// One view resize per settled viewport size (debounced; see the header).
let settle: number | undefined;
const onViewport = (): void => {
  window.clearTimeout(settle);
  settle = window.setTimeout(() => {
    const next = viewportSize();
    if (next === null || (next.width === canvasSize.width && next.height === canvasSize.height)) return;
    canvasSize = next;
    sizeCanvas();
    flow.resize(canvas.clientWidth, canvas.clientHeight);
  }, uiTiming.resizeSettleMs);
};
window.visualViewport?.addEventListener('resize', onViewport);
window.addEventListener('resize', onViewport);
window.addEventListener('orientationchange', onViewport);

createRoot(root).render(
  <StrictMode>
    <App flow={flow} touch={touch} onLanguage={chooseLanguage} account={account} />
  </StrictMode>,
);
